import { removeElementByIndex } from "@helpers/utilities";
import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { State } from "../base/types";
import {
  addDevice as addDeviceApi,
  AddDeviceForm,
  createQRDevice as createQRDeviceApi,
  CreateQRDeviceResponse,
  Device,
  DeviceDescription,
  DeviceMessages,
  DevicesDescriptionForm,
  DevicesDescriptionResponse,
  getDeviceMessages as getDeviceMessagesApi,
  getDevicesDescription as getDevicesDescriptionApi,
  getDevices as getDevicesSmallApi,
  Message,
  updateDevice as updateDeviceApi,
  UpdateDeviceForm,
} from "./api";

export type DeviceType = Omit<DeviceDescription, "updated_at"> & {
  messages?: Message[];
  updated_at?: string;
};

type Devices = Record<string, DeviceType>;

type DevicesState = State<{
  devices: Devices;
  devicesSmall: Device[];
  ids: string[] | null;
  page: number;
  totalCount: number;
  loadingMessages: boolean;
  errorMessages: string | null;
  isUpdating: boolean;
}>;

export const getDevices = createAsyncThunk<
  DevicesDescriptionResponse,
  { userId: string; data: DevicesDescriptionForm }
>("devices/getDevices", async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await getDevicesDescriptionApi(userId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getDeviceMessages = createAsyncThunk<
  DeviceMessages,
  { userId: string; deviceId: string }
>(
  "devices/getDeviceMessages",
  async ({ userId, deviceId }, { rejectWithValue }) => {
    try {
      return await getDeviceMessagesApi(userId, deviceId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const addDevice = createAsyncThunk<
  string,
  { userId: string; data: AddDeviceForm }
>("devices/addDevice", async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await addDeviceApi(userId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const createQRDevice = createAsyncThunk<
  CreateQRDeviceResponse,
  { userId: string }
>("devices/createQRDevice", async ({ userId }, { rejectWithValue }) => {
  try {
    return await createQRDeviceApi(userId);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const updateDevice = createAsyncThunk<
  string,
  { userId: string; data: UpdateDeviceForm }
>("devices/updateDevice", async ({ userId, data }, { rejectWithValue }) => {
  try {
    return await updateDeviceApi(userId, data);
  } catch (error) {
    return rejectWithValue(error);
  }
});

export const getDevicesSmall = createAsyncThunk<Device[], { userId: string }>(
  "devices/getDevicesSmall",
  async ({ userId }, { rejectWithValue }) => {
    try {
      return await getDevicesSmallApi(userId);
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

const initialState: DevicesState = {
  devices: {},
  devicesSmall: [],
  ids: null,
  loading: false,
  error: null,
  page: 1,
  totalCount: 0,
  loadingMessages: false,
  isUpdating: false,
  errorMessages: null,
};

const devicesSlice = createSlice({
  name: "devices",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getDevices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getDevices.fulfilled, (state, action) => {
        state.loading = false;
        state.devices =
          action.payload.device_description?.reduce<Devices>((acc, device) => {
            if (state.devices[device.id]) {
              acc[device.id] = { ...state.devices[device.id], ...device };
            } else {
              acc[device.id] = device;
            }

            return acc;
          }, {}) || {};
        state.ids =
          action.payload.device_description?.map((device) => device.id) || [];
        state.page = action.payload.page;
        state.totalCount = action.payload.count;
      })
      .addCase(getDevices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      .addCase(getDeviceMessages.pending, (state) => {
        state.loadingMessages = true;
        state.errorMessages = null;
      })
      .addCase(getDeviceMessages.fulfilled, (state, action) => {
        state.loadingMessages = false;
        const { message, ...device } = action.payload;
        if (state.devices[device.id]) {
          state.devices[device.id] = {
            ...state.devices[device.id],
            ...device,
            messages: message,
          };
        } else {
          state.devices[device.id] = { ...device, messages: message };
        }
      })
      .addCase(getDeviceMessages.rejected, (state, action) => {
        state.loadingMessages = false;
        state.errorMessages = action.payload as string;
      })
      .addCase(updateDevice.pending, (state) => {
        state.isUpdating = true;
      })
      .addCase(updateDevice.fulfilled, (state, action) => {
        state.isUpdating = false;

        const {
          deviceId,
          delete: deleted,
          ...actionData
        } = action.meta.arg.data;

        if (state.devices[deviceId]) {
          if (deleted) {
            delete state.devices[deviceId];
            if (state.ids?.length) {
              const elementIdx = state.ids.indexOf(deviceId);
              if (elementIdx !== -1) {
                removeElementByIndex(state.ids, elementIdx);
              }
            }
          } else {
            state.devices[deviceId] = {
              ...state.devices[deviceId],
              ...actionData,
            };
          }
        }
      })
      .addCase(updateDevice.rejected, (state) => {
        state.isUpdating = false;
      })
      .addCase(getDevicesSmall.fulfilled, (state, action) => {
        state.devicesSmall = action.payload;
      })
      .addCase(getDevicesSmall.rejected, (state, action) => {
        state.error = action.payload as string;
      });
  },
});

export default devicesSlice.reducer;
